import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Twitter, Instagram, Facebook, Linkedin, Mail, ArrowRight, ChevronDown, BarChart3, Receipt, Globe, Share2, CreditCard, Users, Sparkles, Smartphone } from "lucide-react";
import Logo from "./Logo"; // Import the new Logo component
import Chatbot from "./Chatbot"; // Import the Chatbot component
import RegionSelector from "./RegionSelector";
import { supabase } from "../supabaseClient";

export default function Layout({ children }) {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [darkMode, setDarkMode] = useState(() => {
        const savedTheme = localStorage.getItem("theme");
        // Default to true (dark) if no preference saved, otherwise check if equal to "dark"
        return savedTheme ? savedTheme === "dark" : true;
    });
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        document.documentElement.classList.toggle("dark", darkMode);
        localStorage.setItem("theme", darkMode ? "dark" : "light");
    }, [darkMode]);

    useEffect(() => {
        const checkUser = async () => {
            try {
                const { data: { session } } = await supabase.auth.getSession();
                if (session) {
                    setUser(session.user);
                }
            } catch (err) {
                console.error("Error checking session in layout:", err);
            }
        };

        checkUser();

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            if (session) {
                setUser(session.user);
            } else {
                setUser(null);
            }
        });

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    const handleSignOut = async () => {
        try {
            await supabase.auth.signOut();
            setIsMobileMenuOpen(false);
            navigate("/");
        } catch (err) {
            console.error("Error signing out:", err);
        }
    };

    const [newsletterEmail, setNewsletterEmail] = useState("");
    const [newsletterStatus, setNewsletterStatus] = useState("idle"); // idle, loading, success, error
    const [newsletterMsg, setNewsletterMsg] = useState("");

    const handleNewsletterSubmit = async (e) => {
        e.preventDefault();
        if (!newsletterEmail) return;

        setNewsletterStatus("loading");
        setNewsletterMsg("");

        try {
            const { error } = await supabase
                .from("newsletter_subscribers")
                .insert([{ email: newsletterEmail }]);

            if (error) throw error;

            setNewsletterStatus("success");
            setNewsletterMsg("Thank you! You've been subscribed.");
            setNewsletterEmail("");
            setTimeout(() => {
                setNewsletterStatus("idle");
                setNewsletterMsg("");
            }, 5000);
        } catch (err) {
            console.error("Newsletter error:", err);
            // Handle if email is already subscribed (unique constraint) or general error
            if (err.code === "23505") {
                setNewsletterStatus("error");
                setNewsletterMsg("This email is already subscribed!");
            } else {
                setNewsletterStatus("error");
                setNewsletterMsg("Something went wrong. Please try again.");
            }
            setTimeout(() => {
                setNewsletterStatus("idle");
                setNewsletterMsg("");
            }, 5000);
        }
    };

    const [isFeaturesOpen, setIsFeaturesOpen] = useState(false);
    const [isMobileFeaturesOpen, setIsMobileFeaturesOpen] = useState(false);

    const featureLinks = [
        { name: "Accounting", path: "/features/accounting", icon: <BarChart3 className="w-4 h-4" />, desc: "GST & Bookkeeping" },
        { name: "Mobile Invoicing", path: "/features/mobile-billing", icon: <Smartphone className="w-4 h-4" />, desc: "WhatsApp Bills & Dues" },
        { name: "Expenses", path: "/features/expenses", icon: <Receipt className="w-4 h-4" />, desc: "AI Receipt Scan" },
        { name: "Websites", path: "/features/websites", icon: <Globe className="w-4 h-4" />, desc: "No-Code Builder" },
        { name: "Social Media", path: "/features/social", icon: <Share2 className="w-4 h-4" />, desc: "Viral Growth AI" },
        { name: "POS System", path: "/features/pos", icon: <CreditCard className="w-4 h-4" />, desc: "Retail & Inventory" },
        { name: "Payroll", path: "/features/payroll", icon: <Users className="w-4 h-4" />, desc: "Salary & Taxes" },
    ];

    const navLinks = [
        { name: "Pricing", path: "/pricing" },
        { name: "Enterprise", path: "/enterprise" },
        { name: "About", path: "/about" },
        { name: "Contact", path: "/contact" },
    ];

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isMobileMenuOpen]);

    useEffect(() => {
        setIsMobileMenuOpen(false);
        setIsMobileFeaturesOpen(location.pathname.startsWith("/features"));
    }, [location.pathname]);

    return (
        <div className="min-h-screen flex flex-col font-sans text-primary dark:text-white transition-colors duration-300 overflow-x-hidden">
            {/* Navbar */}
            <nav className="sticky top-0 z-50 w-full glass transition-colors duration-300">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        {/* Logo */}
                        <Link to="/" className="flex items-center gap-2 group">
                            <Logo className="h-12 w-auto transform transition-transform group-hover:scale-105" />
                        </Link>

                        {/* Desktop Nav */}
                        <div className="hidden md:flex items-center gap-8">
                            {/* Features Dropdown */}
                            <div
                                className="relative group h-20 flex items-center"
                                onMouseEnter={() => setIsFeaturesOpen(true)}
                                onMouseLeave={() => setIsFeaturesOpen(false)}
                            >
                                <button
                                    className={`flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-accent h-full
                                        ${location.pathname.startsWith("/features") ? "text-accent" : "text-secondary dark:text-white"}
                                    `}
                                >
                                    Features
                                    <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isFeaturesOpen ? "rotate-180" : ""}`} />
                                </button>

                                {/* Dropdown Menu */}
                                <div className={`absolute top-full left-1/2 -translate-x-1/2 w-[480px] pt-2 transition-all duration-300 ${isFeaturesOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"}`}>
                                    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 p-4 grid grid-cols-2 gap-2 overflow-hidden relative">
                                        {/* Subtle background decoration */}
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 rounded-full blur-3xl pointer-events-none"></div>

                                        <div className="col-span-2 mb-2 px-2 flex items-center justify-between">
                                            <span className="text-[10px] uppercase tracking-widest font-bold text-gray-400">AI Business Suite</span>
                                            <Link to="/features" className="text-[10px] uppercase tracking-widest font-bold text-accent hover:underline">View All</Link>
                                        </div>

                                        {featureLinks.map((subLink) => {
                                            const isActive = location.pathname === subLink.path;
                                            return (
                                                <Link
                                                    key={subLink.name}
                                                    to={subLink.path}
                                                    className={`group/item flex items-start gap-3 p-3 rounded-xl transition-all ${
                                                        isActive
                                                            ? "bg-blue-600 text-white shadow-md shadow-blue-500/20"
                                                            : "hover:bg-gray-50 dark:hover:bg-white/5"
                                                    }`}
                                                >
                                                    <div className={`p-2 rounded-lg transition-colors ${
                                                        isActive
                                                            ? "bg-white/20 text-white"
                                                            : "bg-gray-100 dark:bg-gray-800 text-secondary dark:text-white group-hover/item:text-accent group-hover/item:bg-accent/10"
                                                    }`}>
                                                        {subLink.icon}
                                                    </div>
                                                    <div>
                                                        <div className={`text-sm font-bold transition-colors ${
                                                            isActive
                                                                ? "text-white"
                                                                : "text-primary dark:text-white group-hover/item:text-accent"
                                                        }`}>
                                                            {subLink.name}
                                                        </div>
                                                        <div className={`text-[11px] ${
                                                            isActive
                                                                ? "text-blue-100"
                                                                : "text-gray-500 dark:text-gray-400"
                                                        }`}>
                                                            {subLink.desc}
                                                        </div>
                                                    </div>
                                                </Link>
                                            );
                                        })}

                                        <div className="col-span-2 mt-2 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-center">
                                            <div className="flex items-center gap-2 text-[10px] font-medium text-gray-500">
                                                <Sparkles className="w-3 h-3 text-accent" />
                                                Supercharged by blinqbooks AI
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {navLinks.map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.path}
                                    className={`text-sm font-medium transition-colors hover:text-accent
                                        ${location.pathname === link.path ? "text-accent" : "text-secondary dark:text-white"}
                                    `}
                                >
                                    {link.name}
                                </Link>
                            ))}

                            {/* Region & Currency Selector */}
                            <RegionSelector />

                            {/* Divider */}
                            <div className="h-6 w-px bg-gray-200 dark:bg-gray-700"></div>

                            {/* Dark Mode Toggle */}
                            <button
                                onClick={() => setDarkMode(!darkMode)}
                                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-secondary dark:text-white"
                                aria-label="Toggle Dark Mode"
                            >
                                {darkMode ? (
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                ) : (
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 24.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                    </svg>
                                )}
                            </button>

                            {/* CTA Buttons */}
                            <div className="flex items-center gap-4">
                                {user ? (
                                    <>
                                        <Link
                                            to="/dashboard"
                                            className="text-sm font-medium text-secondary dark:text-white hover:text-accent transition-colors"
                                        >
                                            Dashboard
                                        </Link>
                                        <button
                                            onClick={handleSignOut}
                                            className="px-5 py-2 rounded-full bg-red-500/10 hover:bg-red-500/20 text-red-500 text-sm font-medium transition-all shadow-sm active:scale-95 cursor-pointer"
                                        >
                                            Sign Out
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <Link
                                            to="/login"
                                            className="text-sm font-medium text-secondary dark:text-white hover:text-accent transition-colors"
                                        >
                                            Login
                                        </Link>
                                        <Link
                                            to="/signup"
                                            className="px-6 py-2.5 rounded-full bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-500 hover:from-blue-500 hover:to-emerald-400 text-white text-sm font-bold transition-all shadow-md shadow-blue-500/20 active:scale-95 cursor-pointer"
                                        >
                                            Start Free Trial ✨
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Mobile Menu Button & Region */}
                        <div className="flex items-center gap-2 sm:gap-3 md:hidden">
                            <RegionSelector />
                            <button
                                onClick={() => setDarkMode(!darkMode)}
                                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-secondary dark:text-white"
                            >
                                {darkMode ? (
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                ) : (
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 24.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                    </svg>
                                )}
                            </button>
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="p-2 text-secondary dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                            >
                                {isMobileMenuOpen ? (
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                ) : (
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Menu Backdrop Overlay */}
                {isMobileMenuOpen && (
                    <div
                        className="fixed inset-0 top-20 bg-slate-950/60 backdrop-blur-sm z-40 md:hidden animate-fade-in"
                        onClick={() => setIsMobileMenuOpen(false)}
                        aria-hidden="true"
                    />
                )}

                {/* Mobile Menu Dropdown */}
                {isMobileMenuOpen && (
                    <div className="md:hidden bg-white dark:bg-[#0b0f19] border-t border-b border-slate-200/80 dark:border-slate-800 absolute w-full left-0 top-20 shadow-2xl z-50 animate-fade-in-down max-h-[calc(100vh-5rem)] overflow-y-auto">
                        <div className="px-4 py-4 space-y-2">
                            {/* Mobile Features Accordion */}
                            <div className="rounded-2xl bg-slate-50 dark:bg-slate-900/60 p-1.5 border border-slate-200/60 dark:border-slate-800/60">
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsMobileFeaturesOpen(!isMobileFeaturesOpen);
                                    }}
                                    className="flex items-center justify-between w-full px-3 py-2.5 rounded-xl text-sm sm:text-base font-bold transition-colors text-slate-900 dark:text-white hover:bg-slate-200/50 dark:hover:bg-slate-800/50 cursor-pointer"
                                >
                                    <span className="flex items-center gap-2">
                                        <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                        Features & Apps
                                    </span>
                                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${isMobileFeaturesOpen ? "rotate-180" : ""}`} />
                                </button>

                                {isMobileFeaturesOpen && (
                                    <div className="space-y-1 pt-1.5 border-t border-slate-200/60 dark:border-slate-800/60">
                                        {featureLinks.map((subLink) => {
                                            const isActive = location.pathname === subLink.path;
                                            return (
                                                <Link
                                                    key={subLink.name}
                                                    to={subLink.path}
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                    className={`flex items-center gap-3 px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                                                        isActive
                                                            ? "bg-blue-600 text-white shadow-md shadow-blue-500/20"
                                                            : "text-slate-800 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 shadow-sm"
                                                    }`}
                                                >
                                                    <div className={`p-1.5 rounded-lg transition-colors ${
                                                        isActive
                                                            ? "bg-white/20 text-white"
                                                            : "bg-blue-500/10 text-blue-600 dark:text-blue-400"
                                                    }`}>
                                                        {subLink.icon}
                                                    </div>
                                                    <div>
                                                        <div className={`leading-tight ${isActive ? "text-white font-bold" : ""}`}>{subLink.name}</div>
                                                        <div className={`text-[11px] font-normal ${isActive ? "text-blue-100" : "text-slate-500 dark:text-slate-400"}`}>{subLink.desc}</div>
                                                    </div>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Standard Nav Links */}
                            {navLinks.map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.path}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className={`block px-3.5 py-2.5 rounded-xl text-sm sm:text-base font-bold transition-all ${location.pathname === link.path
                                            ? "bg-blue-600 text-white shadow-md shadow-blue-600/30"
                                            : "text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/5"
                                        }`}
                                >
                                    {link.name}
                                </Link>
                            ))}
                            <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2">
                                {user ? (
                                    <>
                                        <Link
                                            to="/dashboard"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            className="block w-full text-center px-5 py-3.5 rounded-xl bg-accent text-white text-base font-bold hover:bg-accent-hover shadow-md transition-all active:scale-[0.98]"
                                        >
                                            Dashboard
                                        </Link>
                                        <button
                                            onClick={handleSignOut}
                                            className="block w-full text-center px-5 py-3.5 rounded-xl border border-red-500/20 bg-red-500/5 hover:bg-red-500/10 text-red-500 text-base font-bold transition-all shadow-sm active:scale-[0.98] cursor-pointer"
                                        >
                                            Sign Out
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <Link
                                            to="/signup"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            className="block w-full text-center px-5 py-3.5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-500 hover:from-blue-500 hover:to-emerald-400 text-white text-base font-bold shadow-md shadow-blue-500/20 transition-all active:scale-[0.98]"
                                        >
                                            Start Free Trial ✨
                                        </Link>
                                        <Link
                                            to="/login"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            className="block w-full text-center px-5 py-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50 text-slate-800 dark:text-white text-base font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition-all active:scale-[0.98]"
                                        >
                                            Login
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </nav>

            {/* Page content */}
            <main className="flex-1 w-full fade-in">
                {children}
            </main>

            {/* Footer */}
            <footer className="bg-white dark:bg-[#0a0f1c] border-t border-slate-200/60 dark:border-slate-800/60 scroll-mt-12 relative overflow-hidden">
                {/* Decorative background blur */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-72 bg-blue-500/5 dark:bg-blue-500/[0.03] blur-[120px] pointer-events-none rounded-full"></div>

                <div className="max-w-7xl mx-auto py-20 px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 mb-16">
                        {/* Brand Column (lg:col-span-4 for a bit more space) */}
                        <div className="lg:col-span-4 space-y-6">
                            <div className="flex items-center gap-2">
                                <Logo className="h-14 w-auto" />
                            </div>
                            <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed max-w-sm font-medium">
                                The ultimate AI-driven business suite. Everything you need to manage finances, teams, and operations, all in one intelligent platform.
                            </p>
                            <div className="flex gap-4 pt-2">
                                <a href="https://www.facebook.com/blinqbooks/" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/50 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-slate-800 transition-all duration-300 border border-transparent dark:border-slate-700/50" aria-label="Facebook"><Facebook className="w-[18px] h-[18px]" /></a>
                                <a href="https://www.instagram.com/blinqbooks" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/50 text-slate-400 hover:text-pink-500 hover:bg-pink-50 dark:hover:bg-slate-800 transition-all duration-300 border border-transparent dark:border-slate-700/50" aria-label="Instagram"><Instagram className="w-[18px] h-[18px]" /></a>
                                <a href="https://x.com/blinqbooks" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/50 text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-slate-800 transition-all duration-300 border border-transparent dark:border-slate-700/50" aria-label="Twitter"><Twitter className="w-[18px] h-[18px]" /></a>
                                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/50 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-slate-800 transition-all duration-300 border border-transparent dark:border-slate-700/50" aria-label="LinkedIn"><Linkedin className="w-[18px] h-[18px]" /></a>
                            </div>
                        </div>

                        {/* Links Column */}
                        <div className="lg:col-span-2 lg:col-start-6">
                            <h3 className="text-[13px] font-bold text-slate-900 dark:text-white tracking-[0.15em] uppercase mb-6 opacity-90">Product</h3>
                            <ul className="space-y-4">
                                {[
                                    { name: 'Features', path: '/features' },
                                    { name: 'Mobile Invoicing', path: '/features/mobile-billing' },
                                    { name: 'Pricing', path: '/pricing' },
                                    { name: 'Security', path: '/security' },
                                    { name: 'Enterprise', path: '/enterprise' }
                                ].map((item) => (
                                    <li key={item.name}>
                                        <Link to={item.path} className="text-[14px] font-medium text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:translate-x-1 transition-all duration-300 inline-block">
                                            {item.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Company Column */}
                        <div className="lg:col-span-2">
                            <h3 className="text-[13px] font-bold text-slate-900 dark:text-white tracking-[0.15em] uppercase mb-6 opacity-90">Company</h3>
                            <ul className="space-y-4">
                                {['About', 'Careers', 'Blog', 'Contact'].map((item) => (
                                    <li key={item} className="flex items-center gap-2">
                                        <Link to={`/${item.toLowerCase()}`} className="text-[14px] font-medium text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:translate-x-1 transition-all duration-300 inline-block">
                                            {item}
                                        </Link>
                                        {item === 'Careers' && (
                                            <span className="px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 text-[10px] font-bold tracking-wider uppercase border border-blue-200/50 dark:border-blue-500/20">We're Hiring</span>
                                        )}
                                        {item === 'Blog' && (
                                            <span className="px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold tracking-wider uppercase border border-emerald-200/50 dark:border-emerald-500/20">New</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Newsletter Column */}
                        <div className="lg:col-span-3">
                            <h3 className="text-[13px] font-bold text-slate-900 dark:text-white tracking-[0.15em] uppercase mb-6 opacity-90">Stay Updated</h3>
                            <p className="text-[14px] font-medium text-slate-500 dark:text-slate-400 mb-5 leading-relaxed">
                                Get the latest AI insights and product updates delivered to your inbox.
                            </p>
                            <form onSubmit={handleNewsletterSubmit} className="relative flex flex-col gap-2">
                                <div className="relative flex items-center w-full">
                                    <Mail className="absolute left-3 w-[18px] h-[18px] text-slate-400" />
                                    <input
                                        type="email"
                                        value={newsletterEmail}
                                        onChange={(e) => setNewsletterEmail(e.target.value)}
                                        placeholder="Enter your email"
                                        required
                                        disabled={newsletterStatus === "loading"}
                                        className="w-full pl-9 pr-[110px] py-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/50 text-[14px] font-medium text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500/30 outline-none transition-all shadow-sm disabled:opacity-50"
                                    />
                                    <button
                                        type="submit"
                                        disabled={newsletterStatus === "loading" || !newsletterEmail}
                                        className="absolute right-1.5 top-1.5 bottom-1.5 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-bold tracking-wide transition-colors shadow-sm flex items-center gap-1.5 group disabled:opacity-50"
                                    >
                                        {newsletterStatus === "loading" ? "..." : "Join"} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                                    </button>
                                </div>
                                {newsletterMsg && (
                                    <p className={`text-xs font-semibold mt-1 px-1 transition-all ${newsletterStatus === "success"
                                            ? "text-green-600 dark:text-green-400"
                                            : "text-red-500"
                                        }`}>
                                        {newsletterMsg}
                                    </p>
                                )}
                            </form>
                        </div>
                    </div>

                    {/* Bottom Bar */}
                    <div className="border-t border-slate-200/60 dark:border-slate-800/60 pt-8 flex flex-col md:flex-row justify-between items-center gap-6">
                        <p className="text-[14px] font-medium text-slate-500 dark:text-slate-400">
                            &copy; {new Date().getFullYear()} blinqbooks Inc. Made for Modern Accounting.
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
                            <RegionSelector variant="footer" />
                            <Link to="/palette" className="text-[14px] font-medium text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">Design System</Link>
                            <Link to="/privacy" className="text-[14px] font-medium text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">Privacy Policy</Link>
                            <Link to="/terms" className="text-[14px] font-medium text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">Terms of Service</Link>
                        </div>
                    </div>
                </div>
            </footer>

            {/* Chatbot */}
            <Chatbot />
        </div>
    );
}

